import { useEffect, useState } from 'react';
import appConstants from '../constants/appConstants';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import { Form, Button, Table, Spinner, Alert } from 'react-bootstrap';
import {
  FaMoneyCheckAlt,
  FaPrint,
  FaFileExcel,
  FaSearch,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentFeeSummary = () => {
  const { selectedAcademicYear } = useAppContext();
  const [batchId, setBatchId] = useState('');
  const [status, setStatus] = useState('Active');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadBatches();
  }, [selectedAcademicYear]);

  const loadBatches = async () => {
    const token = getAuthToken();

    if (!token) {
      setErrorMsg('Unauthorized! Please log in.');
      setBatches([]);
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      setBatchId('');
      return;
    }

    try {
      const res = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      if (res.data.status === 'success') {
        setBatches(res.data.data || []);
      } else {
        setErrorMsg('Failed to load batches.');
      }
    } catch (err) {
      setBatches([]);
      setErrorMsg('Error loading batches.');
    }
  };

  const fetchStudents = async () => {
    if (!selectedAcademicYear?.id) {
      setErrorMsg('Please select an academic year.');
      setStudents([]);
      return;
    }

    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.post(
        `${appConstants.API_ROOT_URL}get-students-fee-summary`,
        {
          batch_id: batchId,
          status,
        },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      if (res.data.status === 'success') {
        setStudents(res.data.data);
      } else {
        setErrorMsg(res.data.message || 'Failed to load students.');
      }
    } catch (err) {
      setErrorMsg('Error fetching student data.');
    } finally {
      setLoading(false);
    }
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Download Excel handler
  const handleDownloadExcel = () => {
    if (!students.length) return;
    // Prepare header and rows
    const header = [
      'Sl',
      'Ad.No',
      'Name',
      'Status',
      'Total Fees',
      'Due',
      'Paid Fees',
      'Due Balance',
    ];
    const rows = students.map((stu, idx) => [
      idx + 1,
      stu.admission_no,
      stu.student_name,
      stu.status,
      parseFloat(stu.total_fees).toFixed(2),
      parseFloat(stu.due_fees || 0).toFixed(2),
      parseFloat(stu.paid_fees).toFixed(2),
      (parseFloat(stu.due_fees || 0) - parseFloat(stu.paid_fees || 0)).toFixed(
        2,
      ),
    ]);
    // Convert to CSV
    const csvContent = [header, ...rows]
      .map((row) =>
        row
          .map((field) => '"' + String(field).replace(/"/g, '""') + '"')
          .join(','),
      )
      .join('\r\n');
    // Create a blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_fee_summary.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      {/* Print-specific styles for compact, well-aligned print layout */}
      <style>{`
        .fee-summary-table th.adno-col, .fee-summary-table td.adno-col {
          width: 100px !important;
          min-width: 90px !important;
          max-width: 120px !important;
          white-space: nowrap;
          text-align: center !important;
        }
        .fee-summary-table th.name-col, .fee-summary-table td.name-col {
          min-width: 180px !important;
          max-width: 350px !important;
          width: 30vw !important;
          text-align: left !important;
        }
        @media print {
          @page {
            size: A4;
            margin: 18mm 12mm 18mm 12mm;
          }
          body * {
            visibility: hidden !important;
          }
          #fee-summary-print-area, #fee-summary-print-area * {
            visibility: visible !important;
          }
          #fee-summary-print-area {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            page-break-before: auto !important;
            page-break-inside: avoid !important;
          }
          #fee-summary-print-area .print-header {
            display: block !important;
            margin-bottom: 8px !important;
            font-size: 16px !important;
            text-align: center !important;
            color: #000 !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          #fee-summary-print-area table {
            font-size: 12px !important;
            margin: 0 !important;
            width: 100% !important;
            border-collapse: collapse !important;
            page-break-inside: avoid !important;
          }
          #fee-summary-print-area thead {
            page-break-inside: avoid !important;
          }
          #fee-summary-print-area th, #fee-summary-print-area td {
            padding: 2px 6px !important;
            border: 1px solid #333 !important;
            text-align: center !important;
            vertical-align: middle !important;
          }
          #fee-summary-print-area thead th {
            background: #eee !important;
            color: #000 !important;
          }
          .no-print, .no-print * {
            display: none !important;
          }
        }
      `}</style>
      <div className="container mt-4">
        {/* Compact Top Bar */}
        <style>{`
          .fee-summary-table th.adno-col, .fee-summary-table td.adno-col {
            width: 100px !important;
            min-width: 90px !important;
            max-width: 120px !important;
            white-space: nowrap;
            text-align: center !important;
          }
          .fee-summary-table th.name-col, .fee-summary-table td.name-col {
            min-width: 180px !important;
            max-width: 350px !important;
            width: 30vw !important;
            text-align: left !important;
          }
          .fee-summary-topbar {
            background: #343a40;
            color: #fff;
            border-radius: 8px;
            padding: 0.85rem 1rem;
            margin-bottom: 20px;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 16px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.07);
          }
          .fee-summary-topbar-left {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            flex: 1 1 320px;
            min-width: 260px;
          }
          .fee-summary-topbar-title {
            font-size: 18px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            white-space: nowrap;
            flex: 0 0 auto;
          }
          .fee-summary-topbar-filters {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            flex: 1 1 420px;
            min-width: 260px;
          }
          .fee-summary-filter-group {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
            flex: 1 1 180px;
          }
          .fee-summary-filter-group label {
            font-size: 13px;
            font-weight: 500;
            min-width: 40px;
            white-space: nowrap;
          }
          .fee-summary-select {
            width: 100%;
            min-width: 120px;
            height: 32px;
            font-size: 13px;
            background-color: rgba(255,255,255,0.15);
            border: 1px solid rgba(255,255,255,0.3);
            color: #fff;
            border-radius: 4px;
          }
          .fee-summary-primary-button,
          .fee-summary-action-button {
            border-radius: 6px;
            padding: 6px 12px;
            height: 32px;
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            font-weight: 500;
            justify-content: center;
            min-width: 120px;
            flex: 0 0 auto;
          }
          .fee-summary-primary-button {
            background-color: rgba(255,255,255,0.2);
            border: 1px solid rgba(255,255,255,0.3);
            color: #fff;
          }
          .fee-summary-actions {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            justify-content: flex-end;
            flex: 0 0 auto;
          }
          @media (max-width: 900px) {
            .fee-summary-topbar {
              align-items: flex-start;
            }
            .fee-summary-actions {
              width: 100%;
              justify-content: flex-start;
            }
          }
          @media (max-width: 680px) {
            .fee-summary-topbar-left,
            .fee-summary-topbar-filters {
              width: 100%;
            }
            .fee-summary-filter-group {
              flex: 1 1 100%;
            }
            .fee-summary-primary-button,
            .fee-summary-action-button {
              width: 100%;
              min-width: 0;
            }
          }
          @media print {
            @page {
              size: A4;
              margin: 18mm 12mm 18mm 12mm;
            }
            body * {
              visibility: hidden !important;
            }
            #fee-summary-print-area, #fee-summary-print-area * {
              visibility: visible !important;
            }
            #fee-summary-print-area {
              position: static !important;
              left: auto !important;
              top: auto !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              page-break-before: auto !important;
              page-break-inside: avoid !important;
            }
            #fee-summary-print-area .print-header {
              display: block !important;
              margin-bottom: 8px !important;
              font-size: 16px !important;
              text-align: center !important;
              color: #000 !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            #fee-summary-print-area table {
              font-size: 12px !important;
              margin: 0 !important;
              width: 100% !important;
              border-collapse: collapse !important;
              page-break-inside: avoid !important;
            }
            #fee-summary-print-area thead {
              page-break-inside: avoid !important;
            }
            #fee-summary-print-area th, #fee-summary-print-area td {
              padding: 2px 6px !important;
              border: 1px solid #333 !important;
              text-align: center !important;
              vertical-align: middle !important;
            }
            #fee-summary-print-area thead th {
              background: #eee !important;
              color: #000 !important;
            }
            .no-print, .no-print * {
              display: none !important;
            }
          }
        `}</style>

        <div className="fee-summary-topbar no-print">
          <div className="fee-summary-topbar-left">
            <div className="fee-summary-topbar-title">
              <FaMoneyCheckAlt />
              Student Fee Summary
            </div>

            <div className="fee-summary-topbar-filters">
              <div className="fee-summary-filter-group">
                <label>Batch:</label>
                <Form.Select
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  size="sm"
                  className="fee-summary-select"
                >
                  <option value="" style={{ color: '#000' }}>
                    Select Batch
                  </option>
                  {batches.map((batch) => (
                    <option
                      key={batch.id}
                      value={batch.id}
                      style={{ color: '#000' }}
                    >
                      {batch.class_name} {batch.division}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="fee-summary-filter-group">
                <label>Status:</label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  size="sm"
                  className="fee-summary-select"
                >
                  <option value="Active" style={{ color: '#000' }}>
                    Active
                  </option>
                  <option value="Suspend" style={{ color: '#000' }}>
                    Suspend
                  </option>
                </Form.Select>
              </div>

              <Button
                onClick={fetchStudents}
                disabled={!batchId || loading}
                size="sm"
                className="fee-summary-primary-button"
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <FaSearch /> Fetch
                  </>
                )}
              </Button>
            </div>
          </div>

          {students.length > 0 && (
            <div className="fee-summary-actions">
              <Button
                onClick={handleDownloadExcel}
                variant="outline-light"
                size="sm"
                className="fee-summary-action-button"
              >
                <FaFileExcel />
                Excel
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline-light"
                size="sm"
                className="fee-summary-action-button"
              >
                <FaPrint />
                Print
              </Button>
            </div>
          )}
        </div>

        {errorMsg && (
          <Alert variant="danger" className="no-print">
            {errorMsg}
          </Alert>
        )}

        {students.length > 0 && (
          <div id="fee-summary-print-area">
            {/* Modern Table Container */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflowX: 'auto',
              }}
            >
              <div className="table-responsive">
                <Table bordered hover className="fee-summary-table mb-0">
                  <thead
                    style={{
                      background: '#f8f9fa',
                      borderBottom: '3px solid #dee2e6',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          width: '60px',
                        }}
                      >
                        #
                      </th>
                      <th
                        className="adno-col"
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Ad.No
                      </th>
                      <th
                        className="name-col"
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Student Name
                      </th>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Total Fees
                      </th>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Due
                      </th>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Paid Fees
                      </th>
                      <th
                        style={{
                          padding: '12px 15px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                          textAlign: 'center',
                        }}
                      >
                        Due Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((stu, index) => {
                      const balance =
                        parseFloat(stu.due_fees || 0) -
                        parseFloat(stu.paid_fees || 0);
                      return (
                        <tr
                          key={stu.student_id}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="adno-col"
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {stu.admission_no}
                          </td>
                          <td
                            className="name-col"
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              fontWeight: 500,
                            }}
                          >
                            {stu.student_name}
                          </td>
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                            }}
                          >
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: 4,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                backgroundColor:
                                  stu.status === 'Active'
                                    ? '#d4edda'
                                    : '#f8d7da',
                                color:
                                  stu.status === 'Active'
                                    ? '#155724'
                                    : '#721c24',
                              }}
                            >
                              {stu.status}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: 600,
                            }}
                          >
                            ₹{parseFloat(stu.total_fees).toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: 600,
                              color:
                                parseFloat(stu.due_fees || 0) > 0
                                  ? '#dc3545'
                                  : 'inherit',
                            }}
                          >
                            ₹{parseFloat(stu.due_fees || 0).toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: 600,
                            }}
                          >
                            ₹{parseFloat(stu.paid_fees).toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 15px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              fontWeight: 700,
                              color: balance > 0 ? '#dc3545' : '#28a745',
                            }}
                          >
                            ₹{balance.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Summary Statistics */}
            <div
              className="mt-3 text-center no-print"
              style={{ fontSize: '0.9rem', color: '#6c757d' }}
            >
              <strong>Total Students:</strong> {students.length} •
              <strong> Total Fees:</strong> ₹
              {students
                .reduce((sum, stu) => sum + parseFloat(stu.total_fees || 0), 0)
                .toFixed(2)}{' '}
              •<strong> Total Due:</strong> ₹
              {students
                .reduce((sum, stu) => sum + parseFloat(stu.due_fees || 0), 0)
                .toFixed(2)}{' '}
              •<strong> Total Paid:</strong> ₹
              {students
                .reduce((sum, stu) => sum + parseFloat(stu.paid_fees || 0), 0)
                .toFixed(2)}{' '}
              •<strong> Due Balance:</strong> ₹
              {students
                .reduce(
                  (sum, stu) =>
                    sum +
                    (parseFloat(stu.due_fees || 0) -
                      parseFloat(stu.paid_fees || 0)),
                  0,
                )
                .toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentFeeSummary;
